/** Labels de negocio para códigos técnicos del tool logístico (SUP-AGENT-010). */

const STATUS_KEYS: Record<string, string> = {
  SUCCESS: 'supply.supplyAgent.logisticsStatusSuccess',
  SUCCESS_WITH_WARNINGS: 'supply.supplyAgent.logisticsStatusSuccessWarnings',
  NO_ADJUSTMENT_NEEDED: 'supply.supplyAgent.logisticsStatusNoAdjustment',
  NO_FEASIBLE_SOLUTION: 'supply.supplyAgent.logisticsStatusNoFeasible',
  BLOCKED_MISSING_DATA: 'supply.supplyAgent.logisticsStatusBlockedMissing',
  BLOCKED_CONFLICTING_RULES: 'supply.supplyAgent.logisticsStatusBlockedConflict',
  NOT_IMPLEMENTED: 'supply.supplyAgent.logisticsStatusNotImplemented',
};

const STRATEGY_KEYS: Record<string, string> = {
  ROTATION_WEIGHTED_FILL: 'supply.supplyAgent.logisticsStrategyRotation',
  PROPORTIONAL: 'supply.supplyAgent.logisticsStrategyProportional',
  MINIMUM_COMPLIANCE: 'supply.supplyAgent.logisticsStrategyMinCompliance',
  COVERAGE_LIMITED_FILL: 'supply.supplyAgent.logisticsStrategyCoverage',
  MAX_FILL_WITHOUT_EXCEEDING: 'supply.supplyAgent.logisticsStrategyMaxFill',
  BALANCE_COVERAGE: 'supply.supplyAgent.logisticsStrategyBalanceCoverage',
};

const ZONE_KEYS: Record<string, string> = {
  AMBIENT: 'supply.supplyAgent.logisticsZoneAmbient',
  CHILLED: 'supply.supplyAgent.logisticsZoneChilled',
  FROZEN: 'supply.supplyAgent.logisticsZoneFrozen',
  DRY: 'supply.supplyAgent.logisticsZoneDry',
};

const CONSTRAINT_KEYS: Record<string, string> = {
  PALLET_MULTIPLE: 'supply.supplyAgent.logisticsConstraintPallet',
  BASE_MULTIPLE: 'supply.supplyAgent.logisticsConstraintBase',
  BOX_MULTIPLE: 'supply.supplyAgent.logisticsConstraintBox',
  MIN_CONTAINER_FILL: 'supply.supplyAgent.logisticsConstraintContainer',
};

const DIRECTION_KEYS: Record<string, string> = {
  INCREASE_ONLY: 'supply.supplyAgent.logisticsDirectionIncreaseOnly',
  DECREASE_ONLY: 'supply.supplyAgent.logisticsDirectionDecreaseOnly',
  NEAREST: 'supply.supplyAgent.logisticsDirectionNearest',
  INCREASE: 'supply.supplyAgent.logisticsDirectionIncreaseOnly',
  DECREASE: 'supply.supplyAgent.logisticsDirectionDecreaseOnly',
};

const MIX_POLICY_KEYS: Record<string, string> = {
  SEPARATE_GROUPS: 'supply.supplyAgent.logisticsMixSeparateGroups',
  REJECT: 'supply.supplyAgent.logisticsMixReject',
  ALLOW: 'supply.supplyAgent.logisticsMixAllow',
};

type TFunc = (key: string, options?: Record<string, unknown>) => string;

function translateKnown(t: TFunc, map: Record<string, string>, code: string | null | undefined): string {
  if (!code) return '';
  const key = map[String(code).toUpperCase()];
  if (!key) return String(code);
  const label = t(key);
  return label === key ? String(code) : label;
}

export function logisticsStatusLabel(t: TFunc, status: string | null | undefined): string {
  return translateKnown(t, STATUS_KEYS, status);
}

export function logisticsStrategyLabel(t: TFunc, strategy: string | null | undefined): string {
  return translateKnown(t, STRATEGY_KEYS, strategy);
}

export function logisticsConstraintLabel(t: TFunc, constraint: string | null | undefined): string {
  return translateKnown(t, CONSTRAINT_KEYS, constraint);
}

export function logisticsZoneLabel(t: TFunc, zone: string | null | undefined): string {
  return translateKnown(t, ZONE_KEYS, zone);
}

export function logisticsDirectionLabel(t: TFunc, direction: string | null | undefined): string {
  return translateKnown(t, DIRECTION_KEYS, direction);
}

export function logisticsMixPolicyLabel(t: TFunc, policy: string | null | undefined): string {
  return translateKnown(t, MIX_POLICY_KEYS, policy);
}

function formatNumPart(value: unknown): string | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return String(n);
}

/** Resumen de regla aplicada en lenguaje de negocio (junto al Estado). */
export function logisticsRuleSummary(
  t: TFunc,
  rule: Record<string, unknown> | null | undefined,
): string {
  if (!rule) return '';
  const parts: string[] = [];
  const constraint =
    (rule.constraint_type as string) || (rule.type as string) || '';
  if (constraint) {
    parts.push(logisticsConstraintLabel(t, constraint) || constraint);
  }
  const target = formatNumPart(rule.target_value);
  if (target != null) {
    parts.push(t('supply.supplyAgent.logisticsRuleTargetPart', { value: target }));
  }
  const minimum = formatNumPart(rule.minimum_value);
  if (minimum != null) {
    parts.push(t('supply.supplyAgent.logisticsRuleMinPart', { value: minimum }));
  }
  const maximum = formatNumPart(rule.maximum_value);
  if (maximum != null) {
    parts.push(t('supply.supplyAgent.logisticsRuleMaxPart', { value: maximum }));
  }
  const direction = String(rule.adjustment_direction || '');
  if (direction) {
    parts.push(
      t('supply.supplyAgent.logisticsRuleDirectionPart', {
        value: logisticsDirectionLabel(t, direction) || direction,
      }),
    );
  }
  const strategy = String(
    rule.optimization_strategy || (rule.strategy as { code?: string } | undefined)?.code || '',
  );
  if (strategy) {
    parts.push(
      t('supply.supplyAgent.logisticsRuleStrategyPart', {
        value: logisticsStrategyLabel(t, strategy) || strategy,
      }),
    );
  }
  const maxExtra = formatNumPart(rule.max_extra_pct_default);
  if (maxExtra != null) {
    parts.push(t('supply.supplyAgent.logisticsRuleMaxExtraPart', { value: maxExtra }));
  }
  const maxDays = formatNumPart(rule.max_extra_coverage_days_default);
  if (maxDays != null) {
    parts.push(t('supply.supplyAgent.logisticsRuleMaxDaysPart', { value: maxDays }));
  }
  const mixPolicy = String(rule.incompatible_product_policy || '');
  if (mixPolicy) {
    parts.push(
      t('supply.supplyAgent.logisticsRuleMixPart', {
        value: logisticsMixPolicyLabel(t, mixPolicy) || mixPolicy,
      }),
    );
  }
  const containerCode = String(rule.container_code || '').trim();
  if (containerCode) {
    parts.push(t('supply.supplyAgent.logisticsRuleContainerPart', { value: containerCode }));
  }
  return parts.filter(Boolean).join(' · ');
}

/** Sustituye códigos técnicos embebidos en textos del motor (motivos / avisos). */
export function localizeLogisticsTechnicalText(t: TFunc, text: string): string {
  if (!text) return text;
  let out = text;
  const replaceToken = (haystack: string, token: string, replacement: string) =>
    haystack.split(token).join(replacement);

  for (const [code, key] of Object.entries(STRATEGY_KEYS)) {
    const label = t(key);
    if (label && label !== key) {
      out = replaceToken(out, code, label);
    }
  }
  for (const [code, key] of Object.entries(ZONE_KEYS)) {
    const label = t(key);
    if (label && label !== key) {
      out = replaceToken(out, `zona ${code}`, `zona ${label}`);
      out = replaceToken(out, `zone ${code}`, `zona ${label}`);
    }
  }
  for (const [code, key] of Object.entries(CONSTRAINT_KEYS)) {
    const label = t(key);
    if (label && label !== key) {
      out = replaceToken(out, code, label);
    }
  }
  for (const [code, key] of Object.entries(DIRECTION_KEYS)) {
    const label = t(key);
    if (label && label !== key) {
      out = replaceToken(out, code, label);
    }
  }
  for (const [code, key] of Object.entries(MIX_POLICY_KEYS)) {
    const label = t(key);
    if (label && label !== key) {
      out = replaceToken(out, code, label);
    }
  }
  for (const [code, key] of Object.entries(STATUS_KEYS)) {
    const label = t(key);
    if (label && label !== key) {
      out = replaceToken(out, code, label);
    }
  }
  return out;
}
